/**
 * Tracks which posts have already been migrated using localStorage.
 * Uses a content hash to detect duplicates across sessions.
 */

const STORAGE_KEY = 'bch-nostr-migrate:history';

/** Simple hash of content + timestamp for dedup */
function postFingerprint(content: string, createdAt: number): string {
  // Use first 80 chars of content + timestamp to create a reasonably unique key
  const key = `${createdAt}:${content.slice(0, 80)}`;
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = ((hash << 5) - hash + key.charCodeAt(i)) | 0;
  }
  return hash.toString(36);
}

function getHistory(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}

function saveHistory(history: Set<string>): void {
  try {
    // Keep only the most recent 10,000 entries to avoid bloating storage
    const arr = [...history];
    const trimmed = arr.length > 10000 ? arr.slice(arr.length - 10000) : arr;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // localStorage might be full — silently fail
  }
}

/** Check if a post has been previously migrated */
export function wasAlreadyMigrated(content: string, createdAt: number): boolean {
  const fp = postFingerprint(content, createdAt);
  return getHistory().has(fp);
}

/** Mark a post as migrated */
export function markAsMigrated(content: string, createdAt: number): void {
  const fp = postFingerprint(content, createdAt);
  const history = getHistory();
  history.add(fp);
  saveHistory(history);
}

/** Count how many posts in a list are already migrated */
export function countAlreadyMigrated(posts: { content: string; createdAt: number }[]): number {
  const history = getHistory();
  return posts.filter((p) => history.has(postFingerprint(p.content, p.createdAt))).length;
}

/** Filter out posts that were already migrated, returning only new ones */
export function filterNewPosts<T extends { content: string; createdAt: number }>(posts: T[]): T[] {
  const history = getHistory();
  return posts.filter((p) => !history.has(postFingerprint(p.content, p.createdAt)));
}
